import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { PortalHeading } from "@/pages/portal/PortalLayout";
import { Input } from "@/components/ui/input";
import { StatusPill } from "@/components/shared/QuoteTypeBadge";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg, fileUrl, fmtDate, money } from "@/lib/api";

const VersionForm = ({ q, reload, onDone }) => {
  const seed = (q.versions.at(-1)?.items || []).map((i) => ({ label: i.label, kind: i.kind, quantity: i.quantity || 1, unit_price: i.unit_price || "" }));
  const [items, setItems] = useState(seed.length ? seed : [{ label: "", kind: "labour", quantity: 1, unit_price: "" }]);
  const [note, setNote] = useState("");
  const upd = (i, k, v) => setItems(items.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const total = items.reduce((a, i) => a + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
  const issue = async () => {
    try { await api.post(`/admin/quotes/${q.reference}/versions`, { items: items.map((i) => ({ ...i, quantity: Number(i.quantity) })), note: note || null }); toast.success("New version issued"); onDone(); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 rounded-xl bg-[#F4F4F4] p-4 space-y-2" data-testid={`admin-quote-version-form-${q.reference}`}>
      <p className="text-sm font-semibold">Issue fixed-price version v{q.versions.length + 1}</p>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <Input className="col-span-6" value={it.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Line item" data-testid={`admin-line-label-${i}`} />
          <Input className="col-span-2" type="number" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`admin-line-qty-${i}`} />
          <Input className="col-span-3" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} placeholder="Unit USD" data-testid={`admin-line-price-${i}`} />
          <button className="col-span-1" onClick={() => setItems(items.filter((_, idx) => idx !== i))} aria-label="Remove"><Trash2 className="h-4 w-4 text-[#56585E]" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setItems([...items, { label: "", kind: "labour", quantity: 1, unit_price: "" }])} className="flex items-center gap-1 text-sm underline" data-testid="admin-line-add"><Plus className="h-3 w-3" />Add line</button>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to customer (optional)" className="flex-1 min-w-[180px]" data-testid="admin-quote-note-input" />
        <span className="font-bold text-sm">{money(total)}</span>
        <button disabled={items.some((i) => !i.label || !i.unit_price)} onClick={issue} data-testid={`admin-quote-issue-${q.reference}`} className="rounded-full bg-[#020165] text-white px-5 py-2 text-sm font-semibold disabled:opacity-40">Issue to customer</button>
      </div>
    </div>
  );
};

const QuoteRow = ({ q, invoiced, reload }) => {
  const [open, setOpen] = useState(false);
  const invoice = async () => {
    try { await api.post("/admin/invoices", { quote_reference: q.reference }); toast.success("Invoice created"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const latest = q.versions.at(-1);
  return (
    <article className="rounded-2xl bg-white p-5" data-testid={`admin-quote-${q.reference}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]"><p className="text-xs font-bold text-[#F86900]">{q.reference} · {fmtDate(q.created_at)}</p><p className="font-semibold">{q.service_title}</p>
          <p className="text-sm text-[#56585E]">{[q.customer_name, q.customer_email, [q.vehicle?.make, q.vehicle?.model, q.vehicle?.registration].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</p></div>
        <span className="text-sm font-semibold">{latest?.totals?.total ? money(latest.totals.total) : latest?.totals?.total_min ? `${money(latest.totals.total_min)}–${money(latest.totals.total_max)}` : "Assessment"}</span>
        <StatusPill status={q.status} testId={`admin-quote-status-${q.reference}`} />
        {q.status === "approved" && !invoiced && <button onClick={invoice} data-testid={`admin-quote-invoice-${q.reference}`} className="rounded-full bg-[#F86900] text-[#0D141A] px-4 py-1.5 text-sm font-bold">Create invoice</button>}
        {["pending_review", "ready"].includes(q.status) && <button onClick={() => setOpen(!open)} data-testid={`admin-quote-revise-${q.reference}`} className="rounded-full border px-4 py-1.5 text-sm font-semibold">{open ? "Close" : "Price it"}</button>}
      </div>
      {(q.notes || Object.keys(q.answers || {}).length > 0) && <p className="mt-2 text-xs text-[#56585E]">{Object.entries(q.answers || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")} {q.notes && `· Notes: ${q.notes}`}</p>}
      {q.photo_ids?.length > 0 && <div className="mt-2 flex gap-2">{q.photo_ids.map((id) => <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer"><img src={fileUrl(id)} alt="" className="h-14 w-14 rounded object-cover" /></a>)}</div>}
      {q.deposit_transactions?.length > 0 && <p className="mt-2 text-xs text-[#027A48]">Deposit paid (simulated): {q.deposit_transactions.map((t) => money(t.amount)).join(", ")}</p>}
      {open && <VersionForm q={q} reload={reload} onDone={() => setOpen(false)} />}
    </article>
  );
};

export default function AdminQuotes() {
  const { data, reload } = useFetch("/admin/inbox");
  const invoicedRefs = new Set((data?.invoices || []).map((i) => i.quote_reference));
  return (
    <div data-testid="admin-quotes">
      <PortalHeading title="Quotes & invoices" sub="Review requests, issue fixed-price versions and invoice approved work." />
      <div className="space-y-3">{data?.quotes.map((q) => <QuoteRow key={q.reference} q={q} invoiced={invoicedRefs.has(q.reference)} reload={reload} />)}</div>
      <h2 className="font-display font-bold text-xl mt-10 mb-3">Invoices</h2>
      <div className="rounded-2xl bg-white divide-y">
        {data?.invoices.map((i) => (
          <div key={i.reference} className="p-4 flex flex-wrap gap-3 text-sm" data-testid={`admin-invoice-${i.reference}`}>
            <span className="font-bold">{i.reference}</span><span className="text-[#56585E]">{i.customer_email}</span><span className="ml-auto">{money(i.paid)} / {money(i.subtotal)}</span><StatusPill status={i.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
